import html2canvas from 'html2canvas';
// @ts-ignore
import html2pdf from 'html2pdf.js';
import type { jsPDF } from 'jspdf';
import { jsPDF as JsPDFConstructor } from 'jspdf';
import { PDFDocument } from 'pdf-lib';

import {
  adjustBandCaptureForWideTableSlice,
  CHUNKED_PDF_BAND_EST_SLACK_MM,
  CHUNKED_PDF_FIRST_PAGE_EXTRA_SLACK_MM,
  CHUNKED_PDF_ROW_BAND_SLACK_CSS,
  collectTbodyRowBottomsForChunkedPdf,
  cssHeightToMmForChunkedPdf,
  findChunkedContentYOffsetBelow,
  findNextChunkedYOffsetAfterBand,
  findTbodyMetricsScopeRootForChunkedPdf,
  getWideTableSliceHeadExtraCss,
  mmToPageCssPx,
  pickRowAwareBandHeightCssForChunkedPdf,
  placeChunkedBandOnPdf,
  resolveChunkedBandCaptureForChunkedPdf,
} from './pdfSplitWideTablesForClone';
import { collectPdfKeepTogetherRanges, getPdfPageSlices } from './pdfTablePagination';

type TDownloadPDFParams = {
  fileName: string;
  clonedElementClassName: string;
  pagebreakMode?: 'strict' | 'row-friendly' | 'table-friendly';
  clonedNodeFormatter?: (clonedElement: HTMLElement) => void;
  opt?: {
    margin?: number | { top?: number; right?: number; bottom?: number; left?: number };
  };
  isNumericPages?: boolean;
  isShowContent?: boolean;
  chunkedCanvasRender?: {
    enabled?: boolean;
    maxCanvasHeightPx?: number;
  };
  titlePage?: {
    className?: string;
    isShowWithoutMargin?: boolean;
  };
};

type THtml2PdfOptions = {
  margin?: number | [number, number] | [number, number, number, number];
  filename: string;
  image?: { type?: 'jpeg' | 'png' | 'webp'; quality?: number };
  html2canvas?: {
    windowWidth?: number;
    dpi?: number;
    scale?: number;
    useCORS?: boolean;
  };
  jsPDF?: {
    unit?: string;
    format?: string | [number, number];
    orientation?: 'portrait' | 'landscape';
    align?: string;
    baseline?: string;
    putOnlyUsedFonts?: boolean;
  };
  pagebreak?: { mode?: string[] };
};

type TAnchorInfo = {
  title: string;
  page: number;
  file: PDFDocument | null;
};

type TMarginTuple = [number, number, number, number];

const getMargin = (params: TDownloadPDFParams): number | [number, number, number, number] => {
  if (!params.opt?.margin) {
    return 0;
  }
  if (typeof params.opt.margin === 'number') {
    return params.opt.margin;
  }

  const { top = 0, right = 0, bottom = 0, left = 0 } = params.opt.margin;
  return [top, right, bottom, left];
};

/**
 * В режиме chunked клон монтируется в узкий host; глобальный `.container-page.big { max-width: 1320px }`
 * оставляет полосу ~1440px с пустыми полями — таблица верстается уже, чем в html2pdf, ломая ширину и нарезку колонок.
 */
const applyChunkedPdfExportLayoutWidth = (root: HTMLElement, windowWidthPx: number): void => {
  root.style.setProperty('width', `${windowWidthPx}px`);
  root.style.setProperty('max-width', 'none');
  root.style.setProperty('box-sizing', 'border-box');

  root.querySelectorAll<HTMLElement>('.container-page').forEach((el) => {
    el.style.setProperty('max-width', 'none', 'important');
    el.style.setProperty('width', '100%', 'important');
  });
};

const syncCanvasForPdfClone = (source: HTMLCanvasElement, target: HTMLCanvasElement): void => {
  const rect = source.getBoundingClientRect();
  let cssWidth = Math.round(rect.width || source.clientWidth);
  let cssHeight = Math.round(rect.height || source.clientHeight);

  if (cssWidth <= 0 || cssHeight <= 0) return;

  // Опциональный потолок ширины snapshot (data-pdf-canvas-max-width на предке)
  const maxWidth = Number(source.closest('[data-pdf-canvas-max-width]')?.getAttribute('data-pdf-canvas-max-width'));
  if (Number.isFinite(maxWidth) && maxWidth > 0 && cssWidth > maxWidth) {
    cssHeight = Math.round(cssHeight * (maxWidth / cssWidth));
    cssWidth = Math.round(maxWidth);
  }

  const img = document.createElement('img');
  img.src = source.toDataURL('image/png');
  img.alt = '';
  img.dataset.pdfCanvasSnapshot = '1';
  img.style.width = `${cssWidth}px`;
  img.style.height = `${cssHeight}px`;
  img.style.display = 'block';
  img.style.maxWidth = '100%';

  if (target.className) {
    img.className = target.className;
  }

  target.replaceWith(img);
};

const getBlockHeight = (block: HTMLElement, mainElement: HTMLElement): number => {
  const cloneMainElement = mainElement.cloneNode(false) as HTMLElement;
  const wrapper = document.createElement('div');
  wrapper.style.cssText = 'visibility:hidden;position:absolute;left:-99999px;top:0;width: 1000px;';

  const clone = block.cloneNode(true) as HTMLElement;
  wrapper.append(clone);
  cloneMainElement.append(wrapper);
  document.body.append(cloneMainElement);

  const computedStyle = window.getComputedStyle(clone);
  const marginTop = parseFloat(computedStyle.marginTop) || 0;
  const marginBottom = parseFloat(computedStyle.marginBottom) || 0;
  const height = (clone.offsetHeight || 0) + marginTop + marginBottom;

  cloneMainElement.remove();

  return height;
};

const getFileDoc = async (pdf: jsPDF): Promise<PDFDocument> => {
  const pdfBlob = await pdf.output('blob');
  const pdfBytes = await pdfBlob.arrayBuffer();
  return await PDFDocument.load(pdfBytes);
};

const normalizeMarginTuple = (margin?: number | number[]): TMarginTuple => {
  if (typeof margin === 'number') {
    return [margin, margin, margin, margin];
  }
  if (Array.isArray(margin)) {
    if (margin.length >= 4) {
      return [Number(margin[0]) || 0, Number(margin[1]) || 0, Number(margin[2]) || 0, Number(margin[3]) || 0];
    }
    if (margin.length === 2) {
      return [Number(margin[0]) || 0, Number(margin[1]) || 0, Number(margin[0]) || 0, Number(margin[1]) || 0];
    }
  }
  return [0, 0, 0, 0];
};

const PDF_BLANK_PIXEL_MIN_CHANNEL = 250;
const PDF_BLANK_SCAN_BAND_PX = 64;
const PDF_BREAK_SNAP_LIMIT_PX = 16;

const isBlankPixel = (data: Uint8ClampedArray, offset: number): boolean =>
  data[offset + 3] === 0 ||
  (data[offset] >= PDF_BLANK_PIXEL_MIN_CHANNEL &&
    data[offset + 1] >= PDF_BLANK_PIXEL_MIN_CHANNEL &&
    data[offset + 2] >= PDF_BLANK_PIXEL_MIN_CHANNEL);

const isBlankCanvasRow = (context: CanvasRenderingContext2D, width: number, row: number): boolean => {
  const { data } = context.getImageData(0, row, width, 1);

  for (let pixel = 0; pixel < data.length; pixel += 4) {
    if (!isBlankPixel(data, pixel)) return false;
  }

  return true;
};

const getCanvasContentHeight = (canvas: HTMLCanvasElement): number => {
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return canvas.height;

  for (let bandBottom = canvas.height; bandBottom > 0; bandBottom -= PDF_BLANK_SCAN_BAND_PX) {
    const bandTop = Math.max(0, bandBottom - PDF_BLANK_SCAN_BAND_PX);

    let data: Uint8ClampedArray;
    try {
      data = context.getImageData(0, bandTop, canvas.width, bandBottom - bandTop).data;
    } catch {
      return canvas.height;
    }

    for (let pixel = data.length - 4; pixel >= 0; pixel -= 4) {
      if (!isBlankPixel(data, pixel)) return bandTop + Math.floor(pixel / 4 / canvas.width) + 1;
    }
  }

  return canvas.height;
};

const snapPageBreaksToBlankRows = (
  canvas: HTMLCanvasElement,
  pages: { top: number; bottom: number }[],
): { top: number; bottom: number }[] => {
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context || pages.length < 2) return pages;

  const snapped = pages.map((page) => ({ ...page }));

  try {
    for (let index = 0; index < snapped.length - 1; index += 1) {
      const current = snapped[index];
      const lowestRow = Math.max(current.top + 1, current.bottom - PDF_BREAK_SNAP_LIMIT_PX);

      for (let row = current.bottom; row >= lowestRow; row -= 1) {
        if (isBlankCanvasRow(context, canvas.width, row - 1)) {
          current.bottom = row;
          snapped[index + 1].top = row;
          break;
        }
      }
    }
  } catch {
    return pages;
  }

  return snapped;
};

const hasTbodyRowBelowCss = (rowBottomsCss: number[], yOffsetCss: number): boolean =>
  rowBottomsCss.some((bottom) => bottom > yOffsetCss);

const getChunkedCanvasPdfDoc = async (
  mainElement: HTMLElement,
  opt: THtml2PdfOptions,
  params: TDownloadPDFParams,
): Promise<PDFDocument> => {
  const pdf = new JsPDFConstructor(opt.jsPDF as ConstructorParameters<typeof JsPDFConstructor>[0]);
  const [marginTop, marginRight, marginBottom, marginLeft] = normalizeMarginTuple(opt.margin);
  const pageWidthMm = pdf.internal.pageSize.getWidth();
  const pageHeightMm = pdf.internal.pageSize.getHeight();
  const innerWidthMm = pageWidthMm - marginLeft - marginRight;
  const innerHeightMm = pageHeightMm - marginTop - marginBottom;

  const windowWidthPx = Math.max(320, Math.floor(Number(opt.html2canvas?.windowWidth ?? 1440)));
  const scale = Math.max(0.1, Number(opt.html2canvas?.scale ?? 2));
  const maxCanvasHeightPx = Math.max(600, params.chunkedCanvasRender?.maxCanvasHeightPx ?? 7000);
  const maxBandHeightCssPx = Math.max(200, Math.floor(maxCanvasHeightPx / scale));
  const pageHeightCssPx = Math.max(1, (windowWidthPx * innerHeightMm) / Math.max(1, innerWidthMm));
  const totalHeightCssPx = Math.max(
    1,
    Math.ceil(Math.max(mainElement.scrollHeight, mainElement.getBoundingClientRect().height)),
  );
  const hasTbodyRows = mainElement.querySelector('tbody tr') !== null;
  const imageType = opt.image?.type ?? 'jpeg';
  const imageQuality = opt.image?.quality ?? 1;

  let yOffsetCss = 0;
  let currentYmm = 0;

  while (yOffsetCss < totalHeightCssPx - 0.5) {
    const firstPageExtraMm = currentYmm > 0.01 ? CHUNKED_PDF_FIRST_PAGE_EXTRA_SLACK_MM : 0;
    const availableMm = Math.max(1, innerHeightMm - currentYmm - firstPageExtraMm);
    if (availableMm <= 0.01) {
      if (yOffsetCss >= totalHeightCssPx - 0.5) {
        break;
      }
      pdf.addPage();
      currentYmm = 0;
      continue;
    }

    const availableCssByPage = Math.max(1, Math.floor((availableMm / innerHeightMm) * pageHeightCssPx));
    const hardMaxCss = Math.min(maxBandHeightCssPx, totalHeightCssPx - yOffsetCss);
    const maxCssThisPage = Math.min(hardMaxCss, availableCssByPage);

    if (maxCssThisPage <= 0) {
      if (yOffsetCss >= totalHeightCssPx - 0.5) {
        break;
      }
      pdf.addPage();
      currentYmm = 0;
      continue;
    }

    const metricsScopeRoot = hasTbodyRows
      ? findTbodyMetricsScopeRootForChunkedPdf(mainElement, yOffsetCss)
      : mainElement;
    const rowBottomsCss = hasTbodyRows ? collectTbodyRowBottomsForChunkedPdf(metricsScopeRoot, mainElement) : [];

    const headExtraCss = hasTbodyRows ? getWideTableSliceHeadExtraCss(mainElement, yOffsetCss) : 0;
    const firstPageExtraCss =
      firstPageExtraMm > 0 ? mmToPageCssPx(firstPageExtraMm, pageHeightCssPx, innerHeightMm) : 0;
    const maxCssForRows = Math.max(
      1,
      maxCssThisPage - headExtraCss - CHUNKED_PDF_ROW_BAND_SLACK_CSS - firstPageExtraCss,
    );

    const rowAwareBandCss = hasTbodyRows
      ? pickRowAwareBandHeightCssForChunkedPdf(yOffsetCss, maxCssForRows, rowBottomsCss)
      : null;

    let rowBandHeightCss: number;

    if (rowAwareBandCss === null) {
      if (currentYmm > 0.01 && hasTbodyRowBelowCss(rowBottomsCss, yOffsetCss)) {
        pdf.addPage();
        currentYmm = 0;
        continue;
      }
      if (hasTbodyRows) {
        const nextYOffsetCss = findChunkedContentYOffsetBelow(mainElement, yOffsetCss, totalHeightCssPx);
        if (nextYOffsetCss > yOffsetCss + 0.5) {
          yOffsetCss = nextYOffsetCss;
          continue;
        }
        break;
      }
      if (yOffsetCss >= totalHeightCssPx - 0.5) {
        break;
      }
      rowBandHeightCss = Math.max(1, Math.min(maxCssThisPage, totalHeightCssPx - yOffsetCss));
    } else {
      rowBandHeightCss = rowAwareBandCss;
    }

    const captureAdjusted = adjustBandCaptureForWideTableSlice(mainElement, yOffsetCss, rowBandHeightCss);
    const capture = hasTbodyRows
      ? resolveChunkedBandCaptureForChunkedPdf(
          mainElement,
          metricsScopeRoot,
          yOffsetCss,
          rowBandHeightCss,
          captureAdjusted,
        )
      : captureAdjusted;
    const captureStartCss = capture.captureStartCss;
    const bandHeightCss = Math.min(
      Math.max(1, capture.captureHeightCss),
      totalHeightCssPx - captureStartCss,
      maxBandHeightCssPx,
    );

    const estBandMm = cssHeightToMmForChunkedPdf(bandHeightCss, pageHeightCssPx, innerHeightMm);
    if (currentYmm > 0.01 && estBandMm > availableMm - CHUNKED_PDF_BAND_EST_SLACK_MM) {
      pdf.addPage();
      currentYmm = 0;
      continue;
    }

    const canvas = await html2canvas(mainElement, {
      ...(opt.html2canvas ?? {}),
      scale,
      windowWidth: windowWidthPx,
      width: windowWidthPx,
      height: bandHeightCss,
      x: 0,
      y: captureStartCss,
      scrollX: 0,
      scrollY: 0,
      backgroundColor: '#ffffff',
      logging: false,
    });

    const bandHeightMm = (canvas.height * innerWidthMm) / Math.max(1, canvas.width);
    currentYmm = placeChunkedBandOnPdf({
      pdf,
      canvas,
      bandHeightMm,
      imageType,
      imageQuality,
      marginLeft,
      marginTop,
      innerWidthMm,
      innerHeightMm,
      startYmm: currentYmm,
    });

    const bandEndCss = yOffsetCss + rowBandHeightCss;
    yOffsetCss = hasTbodyRows ? findNextChunkedYOffsetAfterBand(metricsScopeRoot, mainElement, bandEndCss) : bandEndCss;
  }

  await addNumericPages(pdf, params, 2, 3);
  return await getFileDoc(pdf);
};

const generatePdfToc = async (
  opt: THtml2PdfOptions,
  mainElement: HTMLElement,
  params: TDownloadPDFParams,
): Promise<TAnchorInfo[]> => {
  const anchors = Array.from(mainElement.querySelectorAll('[data-pdf-anchor]')) as HTMLElement[];

  return await computeAccurateAnchorPages(anchors, opt, mainElement, params);
};

const computeAccurateAnchorPages = async (
  anchors: HTMLElement[],
  opt: THtml2PdfOptions,
  mainElement: HTMLElement,
  params: TDownloadPDFParams,
): Promise<TAnchorInfo[]> => {
  const result: TAnchorInfo[] = [];

  const format = (opt.jsPDF?.format || 'a3').toString().toLowerCase();
  const pageHeightMm = format === 'a3' ? 405 : 297;

  const margins = Array.isArray(opt.margin)
    ? opt.margin
    : [Number(opt.margin || 0), Number(opt.margin || 0), Number(opt.margin || 0), Number(opt.margin || 0)];
  const [marginTop, , marginBottom] = margins;

  const usableHeightMm = pageHeightMm - marginTop - marginBottom;
  const PX_PER_MM = 96 / 25.4;
  const usableHeight = usableHeightMm * PX_PER_MM;

  let totalPages = 3;
  let usedHeight = 0;
  let currentAnchors: HTMLElement[] = [];

  for (let i = 0; i < anchors.length; i++) {
    const anchor = anchors[i];
    const isLastAnchor = i === anchors.length - 1;
    const title = anchor.dataset.pdfAnchor ?? '';
    const anchorHeight = getBlockHeight(anchor, mainElement);
    const file: PDFDocument | null = null;
    let currentAnchorInfo: Omit<TAnchorInfo, 'title'> = {
      page: 0,
      file: file,
    };

    if (usedHeight + anchorHeight > usableHeight) {
      if (currentAnchors.length) {
        result[result.length - 1].file = (
          await getAnchorInfo(currentAnchors, opt, mainElement, params, totalPages)
        ).file;
        currentAnchors = [];
        usedHeight = anchorHeight;
      }

      if (anchorHeight > usableHeight) {
        currentAnchorInfo = await getAnchorInfo([anchor], opt, mainElement, params, totalPages);
        totalPages += currentAnchorInfo.page;
        usedHeight = 0;
      } else {
        totalPages += 1;
        currentAnchors.push(anchor);

        if (isLastAnchor) {
          currentAnchorInfo.file = (await getAnchorInfo(currentAnchors, opt, mainElement, params, totalPages)).file;
        }
      }
    } else {
      currentAnchors.push(anchor);
      usedHeight += anchorHeight;

      if (isLastAnchor) {
        currentAnchorInfo.file = (await getAnchorInfo(currentAnchors, opt, mainElement, params, totalPages)).file;
      }
    }

    result.push({
      file: currentAnchorInfo.file,
      page: totalPages,
      title,
    });
  }

  return result;
};

const getAnchorInfo = async (
  blocks: HTMLElement[],
  opt: THtml2PdfOptions,
  mainElement: HTMLElement,
  params: TDownloadPDFParams,
  pageNumber: number,
): Promise<TAnchorInfo> => {
  const blocksContainer = document.createElement('div');

  blocks.forEach((block) => {
    blocksContainer.appendChild(block);
  });

  const cloneMainElement = mainElement.cloneNode(false) as HTMLElement;
  cloneMainElement.appendChild(blocksContainer);

  const originalCanvases = blocksContainer.querySelectorAll('canvas') as NodeListOf<HTMLCanvasElement>;
  const clonedCanvases = blocksContainer.querySelectorAll('canvas') as NodeListOf<HTMLCanvasElement>;
  originalCanvases.forEach((canvas, index) => {
    const target = clonedCanvases[index];
    if (!target) return;
    syncCanvasForPdfClone(canvas, target);
  });

  const pdf = await getPdfFile(opt, cloneMainElement);
  await addNumericPages(pdf, params, 0, pageNumber);
  const pdfDoc = await getFileDoc(pdf);
  const page = pdfDoc.getPageCount();

  return {
    title: blocks[blocks.length - 1].dataset.pdfAnchor ?? '',
    page,
    file: pdfDoc,
  };
};

const getPdfFile = async (opt: THtml2PdfOptions, element: HTMLElement): Promise<jsPDF> => {
  return await html2pdf().set(opt).from(element).toPdf().get('pdf');
};

const getTableFriendlyPdfFile = async (opt: THtml2PdfOptions, element: HTMLElement): Promise<jsPDF> => {
  const options = { ...opt, pagebreak: { mode: [] }, enableLinks: false };
  const worker = html2pdf().set(options).from(element).toContainer();
  const overlay: HTMLElement = await worker.get('overlay');

  try {
    const pdf = new JsPDFConstructor(opt.jsPDF as ConstructorParameters<typeof JsPDFConstructor>[0]);
    const [marginTop, marginRight, marginBottom, marginLeft] = normalizeMarginTuple(opt.margin);
    const innerWidth = pdf.internal.pageSize.getWidth() - marginLeft - marginRight;
    const innerHeight = pdf.internal.pageSize.getHeight() - marginTop - marginBottom;
    let containerWidth = 0;
    let ranges: ReturnType<typeof collectPdfKeepTogetherRanges> = [];
    let links: { url: string; left: number; top: number; width: number; height: number }[] = [];
    const canvas: HTMLCanvasElement = await worker
      .set({
        html2canvas: {
          ...opt.html2canvas,
          onclone: (_document: Document, container: HTMLElement) => {
            const containerRect = container.getBoundingClientRect();
            containerWidth = containerRect.width;
            ranges = collectPdfKeepTogetherRanges(container, (containerWidth * innerHeight) / innerWidth);
            links = Array.from(container.querySelectorAll<HTMLAnchorElement>('a[href]')).flatMap((link) =>
              Array.from(link.getClientRects(), (rect) => ({
                url: link.href,
                left: rect.left - containerRect.left,
                top: rect.top - containerRect.top,
                width: rect.width,
                height: rect.height,
              })),
            );
          },
        },
      })
      .toCanvas()
      .get('canvas');
    const canvasScale = canvas.width / containerWidth;
    const pageHeight = Math.floor((canvas.width * innerHeight) / innerWidth);
    const pages = snapPageBreaksToBlankRows(
      canvas,
      getPdfPageSlices(
        Math.max(1, getCanvasContentHeight(canvas)),
        pageHeight,
        ranges.map(({ top, bottom }) => ({ top: top * canvasScale, bottom: bottom * canvasScale })),
      ),
    );
    const pageCanvas = document.createElement('canvas');
    pageCanvas.width = canvas.width;
    const context = pageCanvas.getContext('2d')!;

    pages.forEach(({ top, bottom }, pageIndex) => {
      pageCanvas.height = bottom - top;
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      context.drawImage(canvas, 0, top, canvas.width, bottom - top, 0, 0, canvas.width, bottom - top);
      if (pageIndex) pdf.addPage();
      pdf.addImage(
        pageCanvas.toDataURL(`image/${opt.image?.type ?? 'jpeg'}`, opt.image?.quality ?? 1),
        opt.image?.type ?? 'jpeg',
        marginLeft,
        marginTop,
        innerWidth,
        (pageCanvas.height * innerWidth) / canvas.width,
      );
      links.forEach((link) => {
        const linkTop = Math.max(top, link.top * canvasScale);
        const linkBottom = Math.min(bottom, (link.top + link.height) * canvasScale);
        if (linkTop >= linkBottom) return;
        pdf.link(
          marginLeft + (link.left * innerWidth) / containerWidth,
          marginTop + ((linkTop - top) * innerWidth) / canvas.width,
          (link.width * innerWidth) / containerWidth,
          ((linkBottom - linkTop) * innerWidth) / canvas.width,
          { url: link.url },
        );
      });
    });
    return pdf;
  } finally {
    overlay.remove();
  }
};

const addNumericPages = async (
  pdf: jsPDF,
  params: TDownloadPDFParams,
  pagesOffset: number = 0,
  pageStart: number = 1,
) => {
  if (!params.isNumericPages) {
    return;
  }

  const totalPages = pdf.internal.pages.length - 1;
  let currentPageNumber = pageStart;

  for (let i = 1; i <= totalPages; i++) {
    if (i < pagesOffset + 1) {
      continue;
    }

    pdf.setPage(i);
    pdf.setFontSize(12);
    pdf.setTextColor(100);
    pdf.text(
      currentPageNumber.toString(),
      pdf.internal.pageSize.getWidth() / 2,
      pdf.internal.pageSize.getHeight() - 10,
    );

    currentPageNumber++;
  }

  return pdf;
};

const getPdfSimpleTitlePage = async (
  params: TDownloadPDFParams,
  mainElement: HTMLElement,
  baseOpt: THtml2PdfOptions,
) => {
  const titlePage = mainElement.querySelector(`.${params.titlePage?.className}`);

  const titlePageContainer = document.createElement('div');
  titlePageContainer.classList.add(params.clonedElementClassName);
  const titlePageClone = titlePage?.cloneNode(true) as HTMLElement;
  titlePageContainer.appendChild(titlePageClone);

  titlePage?.remove();

  const titlePagePdfBlob = await html2pdf().set(baseOpt).from(titlePageContainer).outputPdf('blob');

  const titlePdfBytes = await titlePagePdfBlob.arrayBuffer();
  return await PDFDocument.load(titlePdfBytes);
};

const getPdfTocDoc = async (
  mainElement: HTMLElement,
  opt: THtml2PdfOptions,
  anchorsInfo: TAnchorInfo[],
): Promise<PDFDocument | null> => {
  const tocElement = mainElement.querySelector('.js-pdf-content');
  const listElement = tocElement?.querySelector('.pdf-content__list');
  const cloneMainElement = mainElement.cloneNode(false) as HTMLElement;

  if (listElement && tocElement) {
    listElement.innerHTML = '';
    const tocItems = anchorsInfo.filter((item) => item.title !== '');
    tocItems.forEach(({ title, page }) => {
      const li = document.createElement('li');
      li.className = 'pdf-content__list-item pdf-content-list-item';

      const titleSpan = document.createElement('span');
      titleSpan.className = 'pdf-content-list-item__title';
      titleSpan.textContent = title;

      const dividerSpan = document.createElement('span');
      dividerSpan.className = 'pdf-content-list-item__divider';

      const pageSpan = document.createElement('span');
      pageSpan.className = 'pdf-content-list-item__page';
      pageSpan.textContent = String(page);

      li.append(titleSpan, dividerSpan, pageSpan);
      listElement.appendChild(li);
    });

    cloneMainElement.append(tocElement);
  }

  const pdf = await getPdfFile(opt, cloneMainElement);
  return await getFileDoc(pdf);
};

const getPdfMainContent = async (
  mainElement: HTMLElement,
  opt: THtml2PdfOptions,
  params: TDownloadPDFParams,
): Promise<PDFDocument> => {
  if (params.isShowContent) {
    const anchorsInfo = await generatePdfToc(opt, mainElement, params);
    const tocDoc = await getPdfTocDoc(mainElement, opt, anchorsInfo);
    const contentDoc = await PDFDocument.create();

    if (tocDoc) {
      const tocPages = await contentDoc.copyPages(tocDoc, tocDoc.getPageIndices());
      tocPages.forEach((page) => contentDoc.addPage(page));
    }

    for (const anchorInfo of anchorsInfo) {
      if (!anchorInfo.file) {
        continue;
      }
      const pages = await contentDoc.copyPages(anchorInfo.file, anchorInfo.file.getPageIndices());
      pages.forEach((page) => contentDoc.addPage(page));
    }

    return contentDoc;
  }

  if (params.chunkedCanvasRender?.enabled && !params.isShowContent) {
    return await getChunkedCanvasPdfDoc(mainElement, opt, params);
  }

  const pdf =
    params.pagebreakMode === 'table-friendly'
      ? await getTableFriendlyPdfFile(opt, mainElement)
      : await getPdfFile(opt, mainElement);
  await addNumericPages(pdf, params, 2, 3);

  return await getFileDoc(pdf);
};

const savePdfFile = async (docs: PDFDocument[], opt: THtml2PdfOptions) => {
  const mergedPdf = await PDFDocument.create();

  for (const doc of docs) {
    const pages = await mergedPdf.copyPages(doc, doc.getPageIndices());
    pages.forEach((page) => mergedPdf.addPage(page));
  }

  const mergedPdfBytes = await mergedPdf.save();
  const normalizedPdfBytes = Uint8Array.from(mergedPdfBytes);
  const blob = new Blob([normalizedPdfBytes], { type: 'application/pdf' });

  saveFile(blob, opt.filename);
};

export const saveFile = async (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadPDF = async (params: TDownloadPDFParams) => {
  const exportContainer = document.querySelector('.export-page') as HTMLElement;

  if (!exportContainer) {
    return;
  }

  const pagebreakMode = params.pagebreakMode === 'row-friendly' ? ['css', 'legacy'] : ['avoid-all', 'css', 'legacy'];

  const baseOpt: THtml2PdfOptions = {
    filename: `${params.fileName}.pdf`,
    image: { type: 'jpeg', quality: 1 },
    html2canvas: {
      windowWidth: 1440,
      dpi: 300,
      scale: 2,
      useCORS: false,
    },
    jsPDF: {
      unit: 'mm',
      format: 'a3',
      orientation: 'portrait',
      align: 'left',
      baseline: 'middle',
      putOnlyUsedFonts: true,
    },
    pagebreak: { mode: pagebreakMode },
  };

  const opt: THtml2PdfOptions = {
    ...baseOpt,
    margin: getMargin(params),
  };

  const html2CanvasWindowWidthPx = Math.max(320, Math.floor(Number(baseOpt.html2canvas?.windowWidth) || 1440));

  const clonedElement = exportContainer.cloneNode(true) as HTMLElement;
  clonedElement.classList.add(params.clonedElementClassName);

  const originalCanvases = exportContainer.querySelectorAll('canvas');
  const clonedCanvases = clonedElement.querySelectorAll('canvas');
  originalCanvases.forEach((element, index) => {
    const target = clonedCanvases[index];
    if (!target) return;
    syncCanvasForPdfClone(element, target);
  });

  if (params.chunkedCanvasRender?.enabled) {
    applyChunkedPdfExportLayoutWidth(clonedElement, html2CanvasWindowWidthPx);
  }

  params.clonedNodeFormatter?.(clonedElement);

  const shouldMountCloneForCapture = Boolean(params.chunkedCanvasRender?.enabled);
  let captureShell: HTMLElement | null = null;

  if (shouldMountCloneForCapture) {
    captureShell = document.createElement('div');
    captureShell.setAttribute('data-app-pdf-capture-shell', '1');
    captureShell.style.cssText = [
      'position:absolute',
      'left:-99999px',
      'top:0',
      `width:${html2CanvasWindowWidthPx}px`,
      'visibility:visible',
      'opacity:1',
      'pointer-events:none',
      'z-index:0',
      'overflow:visible',
      'background:#ffffff',
    ].join(';');
    document.body.appendChild(captureShell);
    captureShell.appendChild(clonedElement);
    clonedElement.offsetHeight;
  }

  try {
    const titlePage = clonedElement.querySelector(`.${params.titlePage?.className}`);
    const docs: PDFDocument[] = [];

    if (params.titlePage?.isShowWithoutMargin && titlePage) {
      const titlePageDoc = await getPdfSimpleTitlePage(params, clonedElement, baseOpt);
      docs.push(titlePageDoc);
    }

    const contentDoc = await getPdfMainContent(clonedElement, opt, params);
    docs.push(contentDoc);

    await savePdfFile(docs, opt);
  } finally {
    captureShell?.remove();
  }

  clonedElement.remove();
};
